<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { getMaterialUsages } from '../material/material-utils';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  assetId: {
    type: String,
    default: '',
  },
  assetUrl: {
    type: String,
    default: '',
  },
  currentTag: {
    type: String,
    default: '',
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
});

const emit = defineEmits(['update', 'close', 'remove']);

// State
const selectedTag = ref(props.currentTag);
const customUsage = ref('');
const inputRef = ref(null);
const popoverRef = ref(null);

// Constants
const POPOVER_WIDTH = 300;
const POPOVER_HEIGHT = 320;
const VIEWPORT_PADDING = 16;

// Get localized material usages
const materialUsages = computed(() => getMaterialUsages());

// Watchers
watch(
  () => props.currentTag,
  (newVal) => {
    selectedTag.value = newVal;
  }
);

watch(
  () => props.show,
  async (show) => {
    if (show) {
      selectedTag.value = props.currentTag;
      customUsage.value = '';
      await nextTick();
    }
  }
);

// Actions
const selectUsage = (usage) => {
  selectedTag.value = usage.label;
  emit('update', { id: props.assetId, tag: usage.label });
  emit('close');
};

const addCustomUsage = () => {
  const value = customUsage.value.trim();
  if (value) {
    selectedTag.value = value;
    emit('update', { id: props.assetId, tag: value });
    customUsage.value = '';
    emit('close');
  }
};

const handleRemove = () => {
  emit('remove', props.assetId);
  emit('close');
};

const handleKeydown = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    addCustomUsage();
  } else if (e.key === 'Escape') {
    e.preventDefault();
    emit('close');
  }
};

// Safe position calculation
const safePosition = computed(() => {
  const { x, y } = props.position;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  const halfWidth = POPOVER_WIDTH / 2;
  let safeX = x;

  if (x - halfWidth < VIEWPORT_PADDING) {
    safeX = halfWidth + VIEWPORT_PADDING;
  }
  if (x + halfWidth > viewportWidth - VIEWPORT_PADDING) {
    safeX = viewportWidth - halfWidth - VIEWPORT_PADDING;
  }

  let safeY = y + 8;
  let showAbove = false;

  if (safeY + POPOVER_HEIGHT > viewportHeight - VIEWPORT_PADDING) {
    showAbove = true;
    safeY = y - POPOVER_HEIGHT - 8;
  }

  if (safeY < VIEWPORT_PADDING) {
    safeY = VIEWPORT_PADDING;
  }

  return { x: safeX, y: safeY, showAbove };
});

const popoverStyle = computed(() => ({
  left: `${safePosition.value.x}px`,
  top: `${safePosition.value.y}px`,
  transform: 'translateX(-50%)',
}));

const isSelected = (usage) => selectedTag.value === usage.label;
</script>

<template>
  <Teleport to="body">
    <!-- Transparent Backdrop for click-outside -->
    <div v-if="show" class="fixed inset-0 z-[99]" @click="emit('close')" />

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-2"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="show"
        ref="popoverRef"
        class="material-popover fixed z-[100] bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl shadow-black/10 overflow-hidden flex flex-col"
        :style="[popoverStyle, { width: `${POPOVER_WIDTH}px` }]"
        @click.stop
        @mousedown.stop
      >
        <!-- Header with Preview -->
        <div class="p-4 pb-3">
          <div class="flex items-center gap-3 mb-3">
            <!-- Image Preview -->
            <div
              class="w-12 h-12 rounded-xl bg-neutral-100 overflow-hidden flex-shrink-0 ring-1 ring-black/5"
            >
              <img
                v-if="assetUrl"
                :src="assetUrl"
                class="w-full h-full object-cover"
                alt="Material preview"
              />
              <div v-else class="w-full h-full flex items-center justify-center">
                <svg
                  class="w-5 h-5 text-neutral-300"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
              </div>
            </div>

            <!-- Title & Current Tag -->
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold text-neutral-800">
                {{ $t('material.selectUsage') }}
              </div>
              <div class="text-xs text-neutral-400 mt-0.5 truncate">
                {{ selectedTag || $t('material.clickToSetUsage') }}
              </div>
            </div>
          </div>

          <div class="h-px bg-neutral-100 w-full"></div>
        </div>

        <!-- Usage Grid -->
        <div class="px-4 pb-3 flex-1 overflow-y-auto custom-scrollbar">
          <div class="grid grid-cols-3 gap-2">
            <button
              v-for="usage in materialUsages"
              :key="usage.id"
              class="group relative p-2.5 rounded-xl border transition-all duration-200 text-left hover:scale-[1.02] active:scale-[0.98]"
              :class="
                isSelected(usage)
                  ? 'bg-gradient-to-br from-neutral-900 to-neutral-800 border-neutral-700 shadow-lg'
                  : 'bg-neutral-50/80 border-neutral-200/60 hover:border-neutral-300 hover:bg-white'
              "
              @click="selectUsage(usage)"
            >
              <!-- Selection indicator -->
              <div
                class="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full transition-all duration-200"
                :class="
                  isSelected(usage)
                    ? 'bg-white shadow-sm'
                    : 'bg-neutral-200 group-hover:bg-neutral-300'
                "
              ></div>

              <!-- Label -->
              <span
                class="text-xs font-medium transition-colors duration-200 block"
                :class="
                  isSelected(usage) ? 'text-white' : 'text-neutral-600 group-hover:text-neutral-800'
                "
              >
                {{ usage.label }}
              </span>
            </button>
          </div>
        </div>

        <!-- Custom Input Section -->
        <div class="px-4 pb-4">
          <!-- Divider with "or" -->
          <div class="flex items-center gap-3 mb-3">
            <div class="h-px bg-neutral-100 flex-1"></div>
            <span class="text-[10px] font-medium text-neutral-300 uppercase tracking-wider">{{
              $t('material.or')
            }}</span>
            <div class="h-px bg-neutral-100 flex-1"></div>
          </div>

          <!-- Custom input -->
          <div class="flex items-center gap-2">
            <input
              ref="inputRef"
              v-model="customUsage"
              type="text"
              :placeholder="$t('material.customUsagePlaceholder')"
              class="flex-1 text-sm px-3 py-2.5 border border-neutral-200/80 rounded-xl bg-white/60 focus:outline-none focus:border-neutral-400 focus:bg-white transition-all duration-200 placeholder:text-neutral-300"
              @keydown="handleKeydown"
              @click.stop
            />
            <button
              @click="addCustomUsage"
              :disabled="!customUsage.trim()"
              class="px-4 py-2.5 bg-neutral-900 text-white text-sm font-medium rounded-xl hover:bg-black transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:shadow-lg active:scale-95"
            >
              {{ $t('material.add') }}
            </button>
          </div>
        </div>

        <!-- Footer with Remove Button -->
        <div
          class="p-3 border-t border-neutral-100 bg-neutral-50/50 flex items-center justify-between"
        >
          <button
            class="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors group"
            :title="$t('common.delete')"
            @click="handleRemove"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="transition-transform group-hover:scale-110"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
          </button>

          <button
            class="px-3 py-2 text-xs font-medium text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 rounded-lg transition-colors"
            @click="emit('close')"
          >
            {{ $t('common.close') || 'Close' }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.material-popover {
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 8px 10px -6px rgba(0, 0, 0, 0.05);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}
</style>
